import { motion, type HTMLMotionProps, type Variants } from 'motion/react'
import { fadeUpVariants, staggerContainerVariants, STAGGER_DELAY, REVEAL_MARGIN } from './variants'
import { useCompactViewport } from './useMediaQuery'
import type { RevealAmount } from './ScrollReveal'

export interface StaggerContainerProps extends Omit<HTMLMotionProps<'div'>, 'initial' | 'whileInView' | 'viewport' | 'variants'> {
  children: React.ReactNode
  /** Seconds between each child. */
  stagger?: number
  /** Seconds before the first child starts. */
  delayChildren?: number
  /** How much of the grid must be in view before it reveals. Defaults to `some`; see ScrollReveal. */
  amount?: RevealAmount
  once?: boolean
  /** Use `animate` on mount instead of waiting for the viewport (for above-the-fold grids). */
  immediate?: boolean
}

/**
 * Parent for a staggered reveal. Children must be <StaggerItem>s
 * (or any motion element that uses the `hidden`/`visible` variant names).
 */
export function StaggerContainer({
  children,
  stagger = STAGGER_DELAY,
  delayChildren = 0.05,
  amount = 'some',
  once = true,
  immediate = false,
  ...rest
}: StaggerContainerProps) {
  const compact = useCompactViewport()
  if (compact) {
    // No reveal animation on phones (see ScrollReveal); children render static too.
    return <motion.div {...rest}>{children}</motion.div>
  }

  const variants: Variants = {
    ...staggerContainerVariants,
    visible: {
      opacity: 1,
      transition: { staggerChildren: stagger, delayChildren },
    },
  }

  return (
    <motion.div
      variants={variants}
      initial="hidden"
      {...(immediate ? { animate: 'visible' } : { whileInView: 'visible', viewport: { once, amount, margin: REVEAL_MARGIN } })}
      {...rest}
    >
      {children}
    </motion.div>
  )
}

export interface StaggerItemProps extends Omit<HTMLMotionProps<'div'>, 'variants'> {
  children: React.ReactNode
  variants?: Variants
}

/**
 * A child of <StaggerContainer>. Fades up by default; static on phones (see ScrollReveal).
 * Pass `variants` to change the desktop motion.
 */
export function StaggerItem({ children, variants, ...rest }: StaggerItemProps) {
  const compact = useCompactViewport()
  if (compact) {
    return <motion.div {...rest}>{children}</motion.div>
  }

  return (
    <motion.div variants={variants ?? fadeUpVariants} {...rest}>
      {children}
    </motion.div>
  )
}
